/**
 * 데이터 포맷팅 유틸리티
 * 날짜, 전화번호 등의 포맷 변환 함수
 */

/**
 * 날짜 문자열을 Date 객체로 변환
 *
 * @example
 * parseDate('2024-01-01') // Date object
 * parseDate(new Date()) // Date object (그대로 반환)
 * parseDate(null) // null
 */
export function parseDate(
  dateString: string | Date | null | undefined
): Date | null {
  if (!dateString) {
    return null;
  }
  if (dateString instanceof Date) {
    return dateString;
  }

  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Date 객체를 ISO 문자열로 변환
 *
 * @example
 * formatDate(new Date('2024-01-01')) // '2024-01-01T00:00:00.000Z'
 * formatDate('2024-01-01') // '2024-01-01' (문자열은 그대로 반환)
 * formatDate(null) // null
 */
export function formatDate(
  date: Date | string | null | undefined
): string | null {
  if (!date) {
    return null;
  }
  if (typeof date === 'string') {
    return date;
  }

  return date.toISOString();
}

/**
 * 날짜를 한국어 형식(YYYY-MM-DD)으로 변환
 *
 * ISO 문자열, Date 객체, 또는 날짜 문자열을 받아서
 * 한국어 형식의 날짜 문자열(예: 2024-01-15)로 변환합니다.
 *
 * @example
 * formatDateKorean('2024-01-15T00:48:06.129Z') // '2024-01-15'
 * formatDateKorean(new Date('2024-01-15')) // '2024-01-15'
 * formatDateKorean('invalid') // 'invalid' (파싱 실패 시 원본 반환)
 * formatDateKorean(null) // ''
 */
export function formatDateKorean(
  date: Date | string | null | undefined
): string {
  if (!date) {
    return '';
  }

  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return typeof date === 'string' ? date : '';
  }

  return dateObj
    .toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\. /g, '-')
    .replace('.', '');
}

/**
 * 날짜를 한국어 긴 형식(YYYY년 MM월 DD일)으로 변환
 *
 * @example
 * formatDateKoreanLong('2024-01-15T00:48:06.129Z') // '2024년 1월 15일'
 * formatDateKoreanLong(new Date('2024-01-15')) // '2024년 1월 15일'
 * formatDateKoreanLong(null) // ''
 */
export function formatDateKoreanLong(
  date: Date | string | null | undefined
): string {
  if (!date) {
    return '';
  }

  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return typeof date === 'string' ? date : '';
  }

  return dateObj.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * 날짜를 한국어 짧은 형식(MM월 DD일)으로 변환
 *
 * @example
 * formatDateKoreanShort('2024-01-15T00:48:06.129Z') // '1월 15일'
 * formatDateKoreanShort(new Date('2024-01-15')) // '1월 15일'
 */
export function formatDateKoreanShort(
  date: Date | string | null | undefined
): string {
  if (!date) {
    return '';
  }

  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) {
    return typeof date === 'string' ? date : '';
  }

  return dateObj.toLocaleDateString('ko-KR', {
    month: 'long',
    day: 'numeric',
  });
}

/**
 * 한국 전화번호 포맷팅 (숫자만 → 하이픈 포함)
 * 9~11자리 모든 유효한 한국 전화번호를 포맷팅합니다.
 *
 * @example
 * formatKoreanPhone('01012345678') // '010-1234-5678'
 * formatKoreanPhone('0212345678') // '02-1234-5678'
 * formatKoreanPhone('021234567')  // '02-123-4567'
 * formatKoreanPhone('0311234567') // '031-123-4567'
 */
export function formatKoreanPhone(phone: string): string {
  if (!phone) {
    return phone;
  }

  // 숫자만 추출
  const numbers = phone.replace(/\D/g, '');

  // 1. 서울 지역번호 (02, 9~10자리)
  if (numbers.startsWith('02')) {
    if (numbers.length === 9) {
      return `${numbers.substr(0, 2)}-${numbers.substr(2, 3)}-${numbers.substr(5, 4)}`;
    } else if (numbers.length === 10) {
      return `${numbers.substr(0, 2)}-${numbers.substr(2, 4)}-${numbers.substr(6, 4)}`;
    }
  }

  // 2. 3자리 시작 번호 (010, 031~064, 070, 080 등)
  if (numbers.length === 11) {
    return `${numbers.substr(0, 3)}-${numbers.substr(3, 4)}-${numbers.substr(7, 4)}`;
  } else if (numbers.length === 10) {
    return `${numbers.substr(0, 3)}-${numbers.substr(3, 3)}-${numbers.substr(6, 4)}`;
  }

  // 포맷팅할 수 없는 경우 원본 반환
  return phone;
}

/**
 * 전화번호 마스킹 (보안)
 *
 * @example
 * maskPhone('010-1234-5678') // '010-****-5678'
 * maskPhone('02-1234-5678') // '02-***-5678'
 */
export function maskPhone(phone: string): string {
  if (!phone) {
    return '';
  }

  const formatted = formatKoreanPhone(phone);
  const parts = formatted.split('-');

  if (parts.length === 3) {
    const firstPart = parts[0] || '';
    const middlePart = parts[1] || '';
    const lastPart = parts[2] || '';

    // 중간 부분을 * 로 마스킹
    return `${firstPart}-${'*'.repeat(middlePart.length)}-${lastPart}`;
  }

  return phone;
}

/**
 * 전화번호 타입 식별
 *
 * @example
 * getPhoneType('010-1234-5678') // 'mobile'
 * getPhoneType('02-1234-5678') // 'landline'
 * getPhoneType('invalid') // 'unknown'
 */
export function getPhoneType(
  phone: string
): 'mobile' | 'landline' | 'unknown' {
  if (!phone) {
    return 'unknown';
  }

  const numbers = phone.replace(/\D/g, '');

  // 휴대폰 번호 (현재는 010만 사용)
  if (numbers.startsWith('010') && numbers.length === 11) {
    return 'mobile';
  }

  // 더 이상 사용하지 않는 휴대폰 번호들
  if (
    (numbers.startsWith('011') ||
      numbers.startsWith('016') ||
      numbers.startsWith('017') ||
      numbers.startsWith('018') ||
      numbers.startsWith('019')) &&
    (numbers.length === 10 || numbers.length === 11)
  ) {
    return 'unknown'; // 더 이상 유효하지 않음
  }

  // 지역번호 (유선전화 및 070)
  const validLandlinePrefixes = [
    '02', '031', '032', '033', '041', '042', '043', '044',
    '051', '052', '053', '054', '055', '061', '062', '063', '064',
    '070', '080'
  ];

  if (validLandlinePrefixes.some(p => numbers.startsWith(p))) {
    return 'landline';
  }

  return 'unknown';
}

/**
 * 이름 마스킹
 *
 * @example
 * maskName('홍길동') // '홍*동'
 * maskName('김철') // '김*'
 * maskName('남궁민수') // '남궁*수'
 */
export function maskName(name: string): string {
  if (!name) return '';

  if (name.length <= 2) {
    return name.charAt(0) + '*';
  }

  if (name.length === 3) {
    return name.charAt(0) + '*' + name.charAt(2);
  }

  // 4글자 이상: 앞2, 뒤1 제외하고 마스킹 (남궁민수 -> 남궁*수)
  // 혹은 단순히 가운데를 마스킹
  // 여기서는 앞 2글자 뒤 1글자 남기고 가운데 * 처리로 구현 (일반적인 4글자 성명 패턴 고려)
  // 하지만 4글자가 성이 2글자일 수도 있고 아닐 수도 있어서,
  // 일반적으로는 앞1, 뒤1 남기고 가운데 마스킹이 안전함.
  // maskName('남궁민수') -> '남**수'
  const first = name.charAt(0);
  const last = name.charAt(name.length - 1);
  const middle = '*'.repeat(name.length - 2);

  return first + middle + last;
}

/**
 * 숫자를 통화 형식(1,000)으로 변환
 *
 * @example
 * formatCurrency(1000) // '1,000'
 */
export function formatCurrency(amount: number | string): string {
  const value = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(value)) return '0';
  return new Intl.NumberFormat('ko-KR').format(value);
}

/**
 * 숫자를 통화 형식(1,000원)으로 변환
 *
 * @example
 * formatCurrencyWithSymbol(1000) // '1,000원'
 */
export function formatCurrencyWithSymbol(amount: number | string): string {
  return `${formatCurrency(amount)}원`;
}

/**
 * 한국 부가세 계산 (사사오입 - 일반 반올림)
 *
 * 공급가액(supplyAmount) = round(총액 / 1.1)
 * 부가세(taxAmount) = 총액 - 공급가액
 *
 * 예)
 * totalAmount = 10,000원
 * supplyAmount = round(10,000 / 1.1) = round(9,090.909...) = 9,091원
 * taxAmount = 10,000 - 9,091 = 909원
 *
 * @param totalAmount - 부가세 포함 총액
 * @returns { supplyAmount: 공급가액, taxAmount: 부가세 }
 */
export function calculateKoreanVAT(totalAmount: number): {
  supplyAmount: number;
  taxAmount: number;
} {
  if (!Number.isFinite(totalAmount) || totalAmount < 0) {
    throw new Error(`Invalid totalAmount: ${totalAmount}`);
  }

  // 공급가액 = round(총액 / 1.1) - 사사오입 (일반 반올림)
  const supplyAmount = Math.round(totalAmount / 1.1);

  // 부가세 = 총액 - 공급가액
  const taxAmount = totalAmount - supplyAmount;

  return { supplyAmount, taxAmount };
}

/**
 * 숫자를 한국어 텍스트로 변환 (견적서/문서용)
 *
 * 특징:
 * - 만 단위 이상에서 공백 삽입 (예: "이십만 이백원")
 * - 천, 백, 십 단위에서 "일" 생략 (예: "백" not "일백")
 * - Backend (PDF 생성) 및 Frontend (모달 표시) 공통 사용
 *
 * @example
 * convertNumberToKorean(0)           // "영"
 * convertNumberToKorean(200)         // "이백"
 * convertNumberToKorean(200200)      // "이십만 이백"
 * convertNumberToKorean(1200000)     // "백이십만"
 * convertNumberToKorean(5000000)     // "오백만"
 * convertNumberToKorean(52000000)    // "오천이백만"
 * convertNumberToKorean(1000000000)  // "십억"
 *
 * @param number - 변환할 숫자
 * @returns 한국어로 변환된 텍스트
 */
export function convertNumberToKorean(number: number): string {
  const KOREAN_UNITS = ['', '십', '백', '천'];
  const KOREAN_DIGITS = ['', '일', '이', '삼', '사', '오', '육', '칠', '팔', '구'];
  const KOREAN_SCALE_UNITS = ['', '만', '억', '조', '경'];

  if (number === 0) return '영';

  const isNegative = number < 0;
  number = Math.abs(number);

  const groups: number[] = [];
  let tempNum = number;

  // 4자리씩 끊어서 groups 배열에 저장
  while (tempNum > 0) {
    groups.unshift(tempNum % 10000);
    tempNum = Math.floor(tempNum / 10000);
  }

  let result = '';

  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const scaleIndex = groups.length - 1 - i;

    if (!group || group === 0) continue;

    // 만, 억, 조, 경 등의 큰 단위 그룹 앞에 공백 추가 (이전 결과가 있을 경우)
    if (result.length > 0) {
      result += ' ';
    }

    // 4자리 그룹을 한국어로 변환
    const groupKorean = convertGroupToKorean(group, KOREAN_DIGITS, KOREAN_UNITS);
    result += groupKorean;

    // 만, 억, 조, 경 단위 추가
    if (scaleIndex > 0 && KOREAN_SCALE_UNITS[scaleIndex]) {
      result += KOREAN_SCALE_UNITS[scaleIndex];
    }
  }

  // 마이너스 표시
  return isNegative ? '마이너스 ' + result : result;
}

/**
 * 숫자를 숫자와 한글 단위가 혼합된 형식으로 변환 (UI 힌트용)
 * 큰 금액이나 수량을 입력할 때 실수를 방지하기 위해 사용합니다.
 *
 * @example
 * formatKoreanNumberMixed(125000000) // "1억 2,500만"
 * formatKoreanNumberMixed(10000)     // "1만"
 * formatKoreanNumberMixed(1234)      // "1,234"
 *
 * @param number - 변환할 숫자
 * @returns 혼합 형식 문자열
 */
export function formatKoreanNumberMixed(number: number): string {
  if (number === 0) return '0';
  if (number < 10000) return formatCurrency(number);

  const units = ['', '만', '억', '조', '경'];
  const result: string[] = [];
  let tempNum = number;

  for (let i = 0; tempNum > 0 && i < units.length; i++) {
    const part = tempNum % 10000;
    if (part > 0) {
      const formattedPart = i === 0 ? formatCurrency(part) : formatCurrency(part);
      result.unshift(`${formattedPart}${units[i]}`);
    }
    tempNum = Math.floor(tempNum / 10000);
  }

  return result.join(' ');
}

/**
 * 4자리 그룹을 한국어로 변환하는 헬퍼 함수
 * @internal
 */
function convertGroupToKorean(
  num: number,
  KOREAN_DIGITS: string[],
  KOREAN_UNITS: string[]
): string {
  if (num === 0) return '';

  let result = '';
  const digits = String(num).padStart(4, '0');

  for (let i = 0; i < 4; i++) {
    const digitChar = digits[i];
    if (!digitChar) continue;

    const digit = parseInt(digitChar, 10);
    if (digit === 0) continue;

    const unitIndex = 3 - i;

    // 천, 백, 십 단위에서 "일" 생략 (예: "백" not "일백")
    if (digit === 1 && unitIndex > 0) {
      result += KOREAN_UNITS[unitIndex];
    } else {
      result += KOREAN_DIGITS[digit];
      if (unitIndex > 0) {
        result += KOREAN_UNITS[unitIndex];
      }
    }
  }

  return result;
}

// ============================================================================
// Spec Utilities (견적서 규격 조합/분리)
// ============================================================================

/**
 * Item spec 필드 타입 (API 데이터)
 */
export interface SpecItem {
  spec1?: string | null;
  spec2?: string | null;
  spec3?: string | null;
  specification?: string | null;
}

/**
 * 분리된 spec 결과 타입
 */
export interface ParsedSpecs {
  spec1?: string;
  spec2?: string;
  spec3?: string;
}

/**
 * spec1, spec2, spec3를 슬래시로 조합하여 단일 문자열로 반환
 * specification 필드를 fallback으로 지원 (폼 데이터용)
 *
 * @example
 * combineSpecs({ spec1: "A4", spec2: "100매", spec3: "접착식" }) // "A4 / 100매 / 접착식"
 * combineSpecs({ specification: "A4, 100매" }) // "A4, 100매"
 * combineSpecs({}) // "-"
 */
export function combineSpecs(item: SpecItem): string {
  // 1. spec1, spec2, spec3 조합 (API 데이터)
  const specs = [item.spec1, item.spec2, item.spec3]
    .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
    .map(s => s.trim());

  if (specs.length > 0) {
    return specs.join(' / ');
  }

  // 2. specification fallback (폼 데이터)
  if (item.specification && item.specification.trim().length > 0) {
    return item.specification.trim();
  }

  return '-';
}

/**
 * 사용자가 입력한 규격/사양 한 줄을 spec 필드로 옮긴다 — **글자 하나 잃지 않는다**.
 *
 * 예전에는 콤마(,)·슬래시(/)로 쪼개 앞 3조각만 spec1~3 에 담아, 네 번째 조각부터 사라지고
 * 숫자 속 쉼표(«2,500매» → «2»)와 태그 속 슬래시(«</b>»)까지 잘렸다(GS A2-02). 화면 안내
 * 예시(«15.6인치, i5, 8GB RAM, SSD 256GB»)부터 4조각이라 그대로 따라 쳐도 잘렸다.
 * 이제 입력 한 줄을 통째로 spec1 에 담는다. 표시(`combineSpecs`)는 spec1 하나면 그대로
 * 돌려주므로 입력한 그대로 보인다.
 *
 * @example
 * parseSpecification("15.6인치, i5, 8GB RAM, SSD 256GB") // { spec1: "15.6인치, i5, 8GB RAM, SSD 256GB" }
 * parseSpecification("A4, 80g, 2,500매, 백색")          // { spec1: "A4, 80g, 2,500매, 백색" }
 * parseSpecification("  A4  ") // { spec1: "A4" }
 * parseSpecification("") // {}
 */
export function parseSpecification(specification?: string | null): ParsedSpecs {
  if (!specification || specification.trim() === '') {
    return {};
  }

  return { spec1: specification.trim() };
}

/**
 * 시간을 상대적인 텍스트로 변환 (예: 1분 전, 2시간 전, 어제, 2023.11.24)
 * 
 * @example
 * formatTimeAgo(new Date()) // "방금 전"
 * formatTimeAgo(new Date(Date.now() - 60000)) // "1분 전"
 */
export function formatTimeAgo(dateString?: string | Date): string {
  if (!dateString) return "시간 정보 없음";
  const now = new Date();
  const date = new Date(dateString);
  const diffInMs = now.getTime() - date.getTime();
  const diffInSec = Math.floor(diffInMs / 1000);
  const diffInMin = Math.floor(diffInSec / 60);
  const diffInHour = Math.floor(diffInMin / 60);
  const diffInDay = Math.floor(diffInHour / 24);

  if (diffInSec < 60) return "방금 전";
  if (diffInMin < 60) return `${diffInMin}분 전`;
  if (diffInHour < 24) return `${diffInHour}시간 전`;
  if (diffInDay === 1) return "어제";
  if (diffInDay < 7) return `${diffInDay}일 전`;

  return formatDateKorean(date);
}

/**
 * 채팅 목록용 날짜 포맷팅 (한국 업계 표준)
 * 
 * - 오늘: 오후 2:30
 * - 어제: 어제
 * - 올해: 12월 1일
 * - 과거: 2025.12.1
 *
 * @example
 * formatChatTime(new Date()) // "오후 2:30"
 * formatChatTime('2024-01-15T00:48:06.129Z') // "2024.1.15"
 */
export function formatChatTime(date: Date | string | null | undefined): string {
  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';

  const now = new Date();
  const isToday =
    dateObj.getFullYear() === now.getFullYear() &&
    dateObj.getMonth() === now.getMonth() &&
    dateObj.getDate() === now.getDate();

  if (isToday) {
    return dateObj.toLocaleTimeString('ko-KR', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    dateObj.getFullYear() === yesterday.getFullYear() &&
    dateObj.getMonth() === yesterday.getMonth() &&
    dateObj.getDate() === yesterday.getDate();

  if (isYesterday) {
    return '어제';
  }

  const isThisYear = dateObj.getFullYear() === now.getFullYear();

  if (isThisYear) {
    return dateObj.toLocaleDateString('ko-KR', {
      month: 'long',
      day: 'numeric',
    });
  }

  // 과거: 2025. 1. 1. -> 2025.1.1 (공백 제거 및 마지막 점 제거)
  return dateObj
    .toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })
    .replace(/\. /g, '.')
    .replace(/\.$/, '');
}
